import { history, useLocation, useModel } from '@umijs/max';
import { message, Space } from 'antd';
import React from 'react';
import {
  getSubsystemDefaultPath,
  getSubsystemEntriesFromPermissions,
  resolveSubsystemCodeByPath,
} from '@/config/poolSystemMenu';
import type { MenuPermissionItem } from '@/utils/permission';
import './style.less';

interface SubSystemItem {
  key: string;
  name: string;
  url: string;
  code?: string;
  icon?: React.ReactNode;
  external?: boolean;
  target?: '_blank' | '_self';
  developingTip?: string;
  resolveUrl?: () => string;
}

const isExternalUrl = (url: string): boolean => {
  return /^(https?:)?\/\//.test(url);
};

/**
 * 子系统入口：严格按当前角色已授权的 type=SUBSYSTEM 渲染。
 * 无授权时不展示兜底全量顶栏（避免权限控制失效）。
 */
const SubSystemEntry: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const location = useLocation();
  const { initialState } = useModel('@@initialState');

  const subSystems = React.useMemo<SubSystemItem[]>(() => {
    const menuData = (initialState?.menuData || []) as MenuPermissionItem[];
    // menuData 优先；若尚未就绪或抽不出子系统，回退 localStorage 扁平权限
    let fromPerm = getSubsystemEntriesFromPermissions(menuData);
    if (!fromPerm.length) {
      fromPerm = getSubsystemEntriesFromPermissions();
    }
    return fromPerm.map((item) => ({
      key: item.code,
      name: item.name,
      url: item.url,
      code: item.code,
      resolveUrl: () => getSubsystemDefaultPath(item.code),
    }));
  }, [initialState?.menuData, location.pathname]);

  /** 按当前路由解析选中子系统，与 item.key（subsystem code）对齐 */
  const activeKey = React.useMemo(() => {
    return resolveSubsystemCodeByPath(location.pathname);
  }, [location.pathname]);

  React.useEffect(() => {
    localStorage.setItem('subSystemActiveKey', activeKey);
  }, [activeKey]);

  const handleSystemClick = (system: SubSystemItem) => {
    const {
      name,
      external,
      target = '_blank',
      developingTip,
      resolveUrl,
    } = system;
    const url = resolveUrl ? resolveUrl() : system.url;

    if (!url) {
      const tip =
        developingTip?.trim() ||
        `「${name}」模块正在开发中，敬请期待。`;
      messageApi.error({ content: tip, duration: 3 });
      return;
    }

    localStorage.setItem('subSystemActiveKey', system.key);

    const isExternal = external || isExternalUrl(url);
    if (isExternal) {
      if (target === '_blank') {
        window.open(url, '_blank');
      } else {
        window.location.href = url;
      }
    } else {
      history.push(url);
    }
  };

  if (!subSystems.length) {
    return null;
  }

  return (
    <div className="sub-system-entry">
      {contextHolder}
      <Space size={0}>
        {subSystems.map((system) => (
          <div
            key={system.key}
            className={`sub-system-item ${activeKey === system.key ? 'is-active' : ''}`}
            onClick={() => handleSystemClick(system)}
          >
            {system.icon}
            <span className="font-bold">{system.name}</span>
          </div>
        ))}
      </Space>
    </div>
  );
};

export default SubSystemEntry;
